import { createContext, use, type ReactNode } from 'react';

/** Entrada do `FORM_FIELDS` do tenant, com as chaves do legado (`formFieldConfiguration.ts`). */
export interface FormFieldsEntry {
  readonly Label?: string;
  readonly Hidden?: boolean;
  readonly Disabled?: boolean;
  readonly Required?: boolean;
  readonly DefaultValue?: unknown;
  readonly Options?: readonly unknown[];
}

/** O objeto `FORM_FIELDS` do config do tenant; aninhado por caminho com ponto, como o `_.get` do legado. */
export type FormFieldsConfig = { readonly [key: string]: unknown };

/** Config normalizada de um campo. `required` indefinido = a tela decide. */
export interface FieldConfig {
  readonly label?: string;
  readonly hidden: boolean;
  readonly disabled: boolean;
  readonly required?: boolean;
  readonly defaultValue?: unknown;
  readonly options?: readonly unknown[];
}

const EMPTY: FormFieldsConfig = {};
const FieldConfigContext = createContext<FormFieldsConfig>(EMPTY);

/** Injeta o `FORM_FIELDS` (vem de `shared/config` na E3). Sem provider, nenhum campo muda. */
export function FieldConfigProvider({ config, children }: { readonly config: FormFieldsConfig | undefined; readonly children: ReactNode }) {
  return <FieldConfigContext value={config ?? EMPTY}>{children}</FieldConfigContext>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Lê o `FORM_FIELDS` por caminho (`bankAccount.number`). Função pura, usada pelo hook e por testes. */
export function resolveFieldConfig(config: FormFieldsConfig, name: string): FieldConfig {
  let node: unknown = config;
  for (const key of name.split('.')) {
    node = isRecord(node) ? node[key] : undefined;
  }
  const entry: FormFieldsEntry = isRecord(node) ? node : {};
  return {
    ...(typeof entry.Label === 'string' && entry.Label.trim() !== '' ? { label: entry.Label } : {}),
    hidden: entry.Hidden === true,
    disabled: entry.Disabled === true,
    ...(typeof entry.Required === 'boolean' ? { required: entry.Required } : {}),
    ...('DefaultValue' in entry ? { defaultValue: entry.DefaultValue } : {}),
    ...(Array.isArray(entry.Options) && entry.Options.length > 0 ? { options: entry.Options } : {}),
  };
}

/** Rótulo, oculto, desabilitado, obrigatório e padrão de um campo, pelo nome, segundo o tenant. */
export function useFieldConfig(name: string): FieldConfig {
  return resolveFieldConfig(use(FieldConfigContext), name);
}
